import { isAnnotationType, getAnnotationRectangle, filterAnnotationExceptHighlights } from 'src/app/core/common';
import { PageTextNotesManager } from '../pdf-utils/page-text-notes-manager';
import { TextDataExtractor } from 'src/app/core/pdf-utils/text-data-exctractors';
import { TextHighlighter } from 'src/app/core/pdf-utils/highlight-utils';
import { InfoTagDataItem } from 'src/app/core/pdf-utils/interfaces';
import { PdfTextIterator } from 'src/app/core/reading-utils/pdf-text-iterator';
import { getMouseLocation } from 'src/app/core/pdf-utils/events.handlers';
import { ReadingUnitType, SpeakingJobType } from 'src/app/core/reading-utils/enums';
import { ReadingAtom } from 'src/app/core/reading-utils/interfaces';
import { HighlightingTypes, PageBoxes, AnnotationTypes, NavigationModes, PdfViewerLayoutModes } from 'src/app/core/pdf-utils/enums';
import { AsgardDocBase } from './asgard-doc-base';
import { IPdfCoords, ILanguage, ICustomTextAreaState, Rectangle } from '../interfaces';
import { TagDataExtractor } from '../pdf-utils/tag-data-extractor';
import { convertSettingsColorToRgb } from '../common';
import { TextNoteManager } from '../pdf-utils/text-note';
import { isRectIntersectRect } from '../common';
import { IAutoSaveDocChanges } from 'src/app/services/autosave/autosave.interface';
import { Core, WebViewerInstance } from '@pdftron/webviewer';


export class AsgardDocPdf extends AsgardDocBase {
  private viewerAPI: WebViewerInstance | null = null;
  private pDFDocUtil: Core.PDFNet.PDFDoc | null = null;
  private infoTagData: InfoTagDataItem | null = null;
  private txtHightlighter: TextHighlighter | null = null;
  private textIterator: PdfTextIterator | null = null;
  private tagDataExtractor: TagDataExtractor | null = null;
  private txtDataExtractor: TextDataExtractor | null = null;
  private textNoteManager: TextNoteManager | null = null;
  private pageTextNotesManager: PageTextNotesManager | null = null;
  private pageIdx: number | null = null;
  private pageCount: number | null = null;
  private iteratorPromise: Promise<void> | null = null;

  constructor(docId: string) {
    super(docId);
  }

  get isDocumentReady(): boolean { return this.viewerAPI !== null && this.txtHightlighter !== null && this.pDFDocUtil !== null; }

  get editorElement(): HTMLElement | null {
    if (!this.viewerAPI) { return null; }
    return this.isDocumentReady ? this.viewerAPI.UI.iframeWindow.frameElement as HTMLIFrameElement : null;
  }
  get editorFrame(): HTMLIFrameElement | null {
    if (!this.viewerAPI) { return null; }
    return this.isDocumentReady ? this.viewerAPI.UI.iframeWindow.frameElement as HTMLIFrameElement : null;
  }
  get annotationManager(): Core.AnnotationManager | null { return this.viewerAPI ? this.viewerAPI.Core.annotationManager : null; }
  get docViewer(): Core.DocumentViewer | null { return this.viewerAPI ? this.viewerAPI.Core.documentViewer : null; }

  async addAnnotations(annotations: Core.Annotations.Annotation[]): Promise<void> {
    await this.initPage();
    if (this.textIterator === null) { return; }
    this.textIterator.addAnnotations(annotations);
  }

  async deleteAnnotations(annotationIds: string[]): Promise<void> {
    await this.initPage();
    if (this.textIterator === null) { return; }
    this.textIterator.deleteAnnotations(annotationIds);
  }

  async updateAnnotations(annotations: Core.Annotations.Annotation[]): Promise<void> {
    await this.initPage();
    if (this.textIterator === null) { return; }
    this.textIterator.updateAnnotations(annotations);
  }

  async goToNextReadingItem(itemType: ReadingUnitType): Promise<void> {
    if (this.hasNextItemToRead(itemType)) {
      const isUpdPageNeed = this.textIterator !== null && this.textIterator.isPageFinished(itemType);

      if (isUpdPageNeed && this.viewerAPI) {
        // this.viewerAPI.goToNextPage();
        if (this.getCurrentPageIdx() + 1 < this.viewerAPI.Core.documentViewer.getPageCount()) {
          this.viewerAPI.Core.documentViewer.setCurrentPage(this.getCurrentPageIdx() + 1, false);
        }

      }

      await this.initPage();
      if (this.textIterator === null) {
        return;
      }

      this.setReadingItem(isUpdPageNeed ? this.textIterator.getFirstItem(itemType) : this.textIterator.getNextItem(itemType));
    }
  }

  async goToPrevReadingItem(itemType: ReadingUnitType): Promise<void> {
    if (this.hasPrevItemToRead()) {
      const isUpdPageNeed = this.textIterator !== null && this.textIterator.isPageStarted(itemType);
      if (isUpdPageNeed && this.viewerAPI) {
        // this.viewerAPI.goToPrevPage();
        if (this.getCurrentPageIdx() > 0) {
          this.viewerAPI.Core.documentViewer.setCurrentPage(this.getCurrentPageIdx() - 1, false);
        }
      }

      await this.initPage();
      if (this.textIterator === null) { return; }
      this.setReadingItem(isUpdPageNeed ? this.textIterator.getLastItem(itemType) : this.textIterator.getPrevItem(itemType));
    }
  }

  async goToFirstItem(itemType: ReadingUnitType): Promise<void> {
    await this.initPage();
    if (this.textIterator === null) { return; }

    this.setReadingItem(this.textIterator.getFirstItem(itemType));
  }

  async goToLastItem(itemType: ReadingUnitType): Promise<void> {
    await this.initPage();
    if (this.textIterator === null) { return; }

    this.setReadingItem(this.textIterator.getLastItem(itemType));
  }

  async doHighlight(words: ReadingAtom[], color: string, itemType: HighlightingTypes, exclusive: boolean = false): Promise<void> {
    await this.initPage();

    this.beforeHighlight(words as ReadingAtom[], color, itemType, exclusive);

    if (this.txtDataExtractor === null || this.pageIdx === null) { return; }
    const pageRect = await this.txtDataExtractor.getPageBox(this.pageIdx, PageBoxes.Crop);

    this.currentHighlight.forEach(x => {
      if (!this.textIterator || !this.txtHightlighter || !this.pageIdx) { return; }

      let rects = x.words.map(y => y.boundary);

      if (this.textIterator.isAlternativeReading) {
        rects = this.textIterator.altHighlightRects;
      }

      // Note 5 nov 2024
      // for visibility, make sure that unit highlighting has an alpha of 0.5
      // while word highlighting can keep the default 0.95%
      // only here, because text documents should keep working as before
      const alpha = (x.itemType === HighlightingTypes.Line || x.itemType === HighlightingTypes.Sentence) ? 0.5 : 0.95;

      if (this.viewerAPI !== null) {
        this.txtHightlighter.doHighlight({
          pageNumber: this.pageIdx,
          pageHeight: pageRect.y2,
          pageXOffset: pageRect.x1,
          color: convertSettingsColorToRgb(this.viewerAPI, x.color, alpha),
          rects: rects as Rectangle[],
        },
          x.itemType
        );
      }
    });
  }

  async currentPageHasText(): Promise<boolean> {
    await this.initPage();
    if (this.textIterator === null) { return Promise.resolve(false); }
    return Promise.resolve(this.textIterator.hasWordData);
  }

  async highlightWordBeforeTextNote(annotation: Core.Annotations.Annotation, lastHighlitedWordIdx: number | null): Promise<number | null> {
    if (!annotation || !this.textIterator || !this.txtHightlighter) {
      this.clearHighlightingWordBeforeTextNote();
      return Promise.resolve(null);
    }

    await this.initPage();
    const word = this.textIterator.getWordBeforeTextNote(annotation.Id);

    const blackColor = '#000000';

    if (word && word.boundary && this.txtDataExtractor && this.pageIdx && (!lastHighlitedWordIdx ||
      lastHighlitedWordIdx !== word.itemIdx ||
      !this.txtHightlighter.isWordBeforeTextNoteHighlightExist())
    ) {
      const pageRect = await this.txtDataExtractor.getPageBox(this.pageIdx, PageBoxes.Crop);
      if (this.viewerAPI !== null) {
        this.txtHightlighter.doHighlight({
          rects: [word.boundary],
          pageNumber: this.pageIdx,
          color: convertSettingsColorToRgb(this.viewerAPI, blackColor, 0.25),
          pageHeight: pageRect.y2,
          pageXOffset: pageRect.x1,
        },
          HighlightingTypes.WordBeforeTextNote);
      }

      return Promise.resolve(word.itemIdx);
    }

    return Promise.resolve(null);
  }

  async selectReadingUnitByClick(e: MouseEvent, unit: ReadingUnitType, isOrderedReading: boolean = false): Promise<void> {
    const coords = this.convertMouseCoordsIntoPdfCoords(e);
    if (!coords || !this.textIterator) { return; }

    if (this.pageIdx !== coords.pageIdx) {
      this.pageIdx = coords.pageIdx;
    }

    await this.initPage();

    const items = this.textIterator.getItemClosestToPoint(unit, coords.clientX, coords.clientY, isOrderedReading);

    this.setReadingItem(items ? items : [], true);
  }

  async selectHoverWord(e: MouseEvent): Promise<void> {
    const coords = this.convertMouseCoordsIntoPdfCoords(e);
    if (!coords || !this.textIterator) { return; }

    if (this.pageIdx !== coords.pageIdx) {
      this.pageIdx = coords.pageIdx;
    }
    if (coords.clientX >= 0 && coords.clientY >= 0) {
      await this.initPage();
      this.setReadingItem([this.textIterator.getWordByPoint(coords.clientX, coords.clientY)]);
    } else {
      this.setReadingItem(null);
    }
  }

  async selectReadingUnitByRectangle(rectangleAnnotation: Core.Annotations.Annotation): Promise<void> {
    if (!this.textIterator) {
      await this.initPage();
    }

    if (this.textIterator && this.txtDataExtractor && this.pageIdx) {
      if (this.textIterator.pageIdx !== rectangleAnnotation.PageNumber) {
        this.pageIdx = rectangleAnnotation.PageNumber;
        await this.initPage();
      }

      const pageRect = await this.txtDataExtractor.getPageBox(this.pageIdx, PageBoxes.Crop);
      const rect = getAnnotationRectangle(rectangleAnnotation, pageRect.x1, pageRect.y2);
      let sntncIndex = 0;

      if (rect) {
        const newReadingItem = this.textIterator.getWordsInRectangle(rect);

        if (newReadingItem) {
          newReadingItem.forEach(x => {
            x.text.search(/(\.|\!|\?)/) >= 0 ? x.sntncIndex = sntncIndex++ : x.sntncIndex = sntncIndex;
          });

          this.setReadingItem(newReadingItem);
        }
      }
    }
  }

  async startNextItemFrom(itemType: ReadingUnitType): Promise<void> {
    return this.goToNextReadingItem(itemType);
  }

  async setSelectionToReadingItem(): Promise<void> {
    await this.initPage();

    if (!this.txtDataExtractor || !this.docViewer || !this.pageIdx || !this.textIterator) { return; }
    const pageRect = await this.txtDataExtractor.getPageBox(this.pageIdx, PageBoxes.Crop);
    const quads = this.docViewer.getSelectedTextQuads(this.getCurrentPageIdx());
    let newReadingItem: ReadingAtom[] = [];

    for (const quad of quads) {
      const height = quad.y1 - quad.y4;
      const width = quad.x2 - quad.x1;
      const x = pageRect.x1 + quad.x1;
      const y = (pageRect.y2 - height) - quad.y4;
      const rect: Rectangle = {
        x: x, y: y,
        width, height
      };

      // if there were already reading items in the list, continue with the symbPos
      // otherwise start at 0
      let startSymbPos = 0;
      if (newReadingItem.length > 0) {
        const last = newReadingItem[newReadingItem.length - 1];
        // new word starts at start of previous word + the number of character in previous word
        startSymbPos = last.symbPos + last.text.length + 1;
      }

      const quadWords = this.textIterator.getWordsInRectangle(rect, true, startSymbPos);

      newReadingItem = quadWords.length > 0 ? newReadingItem.concat(quadWords) : newReadingItem;
    }

    let sntncIndex = 0;
    newReadingItem.forEach(x => {
      x.text.search(/(\.|\!|\?)/) >= 0 ? x.sntncIndex = sntncIndex++ : x.sntncIndex = sntncIndex;
    });

    if (newReadingItem !== null && newReadingItem.length > 0) {
      this.setReadingItem(newReadingItem);
      this.docViewer.clearSelection();
    }
  }

  async getSpeakingJobType(): Promise<SpeakingJobType> {
    await this.initPage();

    if (!this.textIterator) { return SpeakingJobType.Document; }
    return this.textIterator.isAlternativeReading ? SpeakingJobType.AltText : SpeakingJobType.Document;
  }

  setCurrentPage(pageNumber: number): void {
    if (!this.viewerAPI) { return; }
    this.viewerAPI.Core.documentViewer.setCurrentPage(pageNumber, false);
  }

  refreshReadingItem(): void {
    this.clearTextHighlight(true);
    super.setReadingItem([]);
  }

  clearHighlightingWordBeforeTextNote(): void {
    if (!this.txtHightlighter) { return; }
    this.txtHightlighter.removeHighlights(HighlightingTypes.WordBeforeTextNote);
  }

  hasSelection(): boolean {
    if (!this.viewerAPI) { return false; }
    const txt = this.viewerAPI.Core.documentViewer.getSelectedText(this.getCurrentPageIdx());
    return txt !== null && txt.length > 0;
  }

  hasNextItemToRead(itemType: ReadingUnitType): boolean {
    if (this.readingItem === null || this.textIterator === null) {
      return false;
    }

    return this.hasReadingItem && !(
      this.pageIdx === this.pageCount &&
      this.textIterator.isPageFinished(itemType) &&
      this.readingItem[this.readingItem.length - 1].isLast
    );
  }

  hasPrevItemToRead(): boolean {
    if (!this.pageIdx) { return false; }
    return this.hasReadingItem && this.pageIdx >= 1;
  }

  moveCursorToCurrentItemStart(itemType: ReadingUnitType): void {
    if (this.hasReadingItem) {
      this.goToNextReadingItem(itemType);
    }
  }

  setDocumentOptins(lngMpr: (...args) => ILanguage, instance: WebViewerInstance,
    pDFDocUtil: Core.PDFNet.PDFDoc, pageCount: number, pageIdx: number): void {
    this.viewerAPI = instance;
    this.pDFDocUtil = pDFDocUtil;
    this.pageCount = pageCount;
    this.pageIdx = pageIdx;
    this.txtHightlighter = new TextHighlighter(this.viewerAPI);

    this.txtDataExtractor = new TextDataExtractor(this.viewerAPI.Core.PDFNet, this.pDFDocUtil);
    this.tagDataExtractor = new TagDataExtractor(this.viewerAPI.Core.PDFNet, this.pDFDocUtil);

    this.textNoteManager = new TextNoteManager(this.viewerAPI,
      this.editorElement as HTMLIFrameElement,
      this.viewerAPI.Core.documentViewer,
      this.viewerAPI.Core.annotationManager);

    this.pageTextNotesManager = new PageTextNotesManager(this.viewerAPI.Core.documentViewer.getAnnotationManager());

    super.setDocumentOptins(lngMpr);
  }

  clearTextHighlight(force: boolean = true, clicked: boolean = false): void {
    super.clearTextHighlight(force, clicked);
    if (!this.txtHightlighter) { return; }
    this.txtHightlighter.removeHighlights();
  }

  getCurrentPageIdx(): number {
    return this.pageIdx || 0;
  }

  setCurrentPageIdx(value: number): void {
    this.pageIdx = value;
  }

  isItemTextNote(itemIdx: number): boolean {
    return this.textIterator !== null && this.textIterator.isItemTextNote(itemIdx);
  }

  focus(): void {
    if (this.isDocumentReady && this.viewerAPI) {
      this.viewerAPI.UI.iframeWindow.focus();
    }
  }

  scrollToWord(word: ReadingAtom): void {
    if (word && word.boundary && this.viewerAPI) {
      const elem = this.viewerAPI.Core.documentViewer.getScrollViewElement();

      if (elem) {
        let top = elem.scrollTop;
        let left = elem.scrollLeft;

        const pdfStart = {
          x: word.boundary.x,
          y: word.boundary.y,
        };

        const pdfEnd = {
          x: word.boundary.x + word.boundary.width,
          y: word.boundary.y + word.boundary.height,
        };

        const windowStart = this.convertPdfCoordsToWindowCoords(pdfStart.x, pdfStart.y);
        const windowEnd = this.convertPdfCoordsToWindowCoords(pdfEnd.x, pdfEnd.y);

        let needToScroll = false;

        if (windowStart && windowEnd) {
          // const zeroIndexedPageIndex = this.getCurrentPageIdx() - 1;
          const pageRect = this.getPageRect(this.getCurrentPageIdx());

          if (pageRect) {
            if (windowStart.y > elem.scrollTop + elem.clientHeight ||
              windowStart.y < elem.scrollTop) {

              const pageYOffset = pageRect.bottom + elem.scrollTop;

              if (windowEnd.y + elem.clientHeight > pageYOffset) {
                top = pageYOffset - elem.clientHeight;
              } else {
                top = windowEnd.y;
              }

              needToScroll = true;
            }

            if (windowEnd.x > elem.scrollLeft + elem.clientWidth ||
              windowStart.x < elem.scrollLeft) {

              const pageYOffset = pageRect.bottom + elem.scrollTop;

              if (windowEnd.y + elem.clientHeight > pageYOffset) {
                top = pageYOffset - elem.clientHeight;
              } else {
                top = windowEnd.y;
              }

              left = windowStart.x;
              needToScroll = true;
            }

            if (needToScroll) {
              elem.scrollTo({ top, left, behavior: 'smooth' });
            }
          }
        }
      }
    }
  }

  getShowedDocRect(): { x1: number, y1: number, x2: number, y2: number } | null {
    if (!this.isDocumentReady || !this.viewerAPI) {
      return null;
    }
    // const zeroIndexedPageIndex = this.getCurrentPageIdx() - 1;
    const elem = this.viewerAPI.Core.documentViewer.getScrollViewElement();

    if (elem) {
      const top = elem.scrollTop;
      const left = elem.scrollLeft;

      // y coords turned over in pdf
      // const { x: x1, y: y2 } = this.convertWindowCoordsToPdfCoords(
      //     { x: left, y: top }, zeroIndexedPageIndex);
      // const { x: x2, y: y1 } = this.convertWindowCoordsToPdfCoords(
      //     { x: left + elem.clientWidth, y: top + elem.clientHeight }, zeroIndexedPageIndex);

      const leftTopCoords = this.convertWindowCoordsToPdfCoords(
        { x: left, y: top }, this.getCurrentPageIdx());
      const rightBottomCoords = this.convertWindowCoordsToPdfCoords(
        { x: left + elem.clientWidth, y: top + elem.clientHeight }, this.getCurrentPageIdx());

      if (!leftTopCoords || !rightBottomCoords) {
        return null;
      }

      return {
        x1: Math.floor(leftTopCoords.x), // x1
        y1: Math.floor(rightBottomCoords.y), // y1
        x2: Math.ceil(rightBottomCoords.x), // x2
        y2: Math.ceil(leftTopCoords.y) // y2
      };
    }

    return null;
  }

  selectedText(): string {
    if (!this.viewerAPI || !this.pageIdx) { return ''; }
    const docViewer = this.viewerAPI.Core.documentViewer;
    const text = docViewer.getSelectedText();
    return text;
  }

  openNewTextNoteByClick(
    e: MouseEvent,
    customcoords: boolean = false,
    fontSize: number,
    annotType: AnnotationTypes | null,
  ): ICustomTextAreaState | void {
    if (!this.textNoteManager) { return; }
    return this.textNoteManager.openNewTextNoteByClick(e, customcoords, fontSize, annotType);
  }

  convertAnnotationToEditableElement(
    e: Core.Annotations.FreeTextAnnotation,
  ): ICustomTextAreaState | null {
    if (!this.textNoteManager) { return null; }
    return this.textNoteManager.convertAnnotationToEditableElement(e);
  }

  scrollToBeginningOfAnotation(e: Core.Annotations.FreeTextAnnotation): void {
    this.textNoteManager?.scrollToBeginningOfAnnotation(e);
  }

  async drawTextAnnotation(e: ICustomTextAreaState): Promise<Core.Annotations.Annotation | null> {
    if (!this.textNoteManager) { return null; }
    return this.textNoteManager.drawTextAnnotation(e);
  }

  rawDeleteAnnotations(annots: Core.Annotations.Annotation[]): Promise<void> {
    if (!this.textNoteManager) { return Promise.reject(); }
    return this.textNoteManager.rawDeleteAnnotations(annots);
  }

  rawAddAnnotations(annots: Core.Annotations.Annotation[]): Promise<void> {
    if (!this.textNoteManager) { return Promise.reject(); }
    return this.textNoteManager.rawAddAnnotations(annots);
  }

  showHideTextAnnotations(show: boolean, annotTypes: AnnotationTypes[]): void {
    if (this.viewerAPI) {
      const annotManager = this.viewerAPI.Core.annotationManager;
      const anotList = annotManager.getAnnotationsList().filter(e => {
        if (isAnnotationType(e, annotTypes)) {
          return e;
        }
      });
      if (anotList && anotList.length) {
        if (show) {
          annotManager.showAnnotations(anotList);
        } else {
          annotManager.hideAnnotations(anotList);
        }
      }
    }
  }

  async initPage(): Promise<void> {
    if (!this.isDocumentReady) {
      return Promise.reject();
    }

    if (this.iteratorPromise == null && (this.textIterator === null || this.textIterator.pageIdx !== this.pageIdx)) {
      this.iteratorPromise = new Promise(async (resolve, reject) => {
        try {
          if (this.txtDataExtractor && this.pageIdx && this.tagDataExtractor && this.pageTextNotesManager) {
            const pageXml = await this.txtDataExtractor.extractPageXml(this.pageIdx);
            this.infoTagData = await this.tagDataExtractor.getPageData(this.pageIdx);
            const pageRect = await this.txtDataExtractor.getPageBox(this.pageIdx, PageBoxes.Crop);

            this.pageTextNotesManager.setPageOptions({ idx: this.pageIdx, xOffset: pageRect.x1, height: pageRect.y2 });
            this.textIterator = new PdfTextIterator(
              pageXml, this.pageIdx, 'text/xml', this.infoTagData, this.pageTextNotesManager
            );

            resolve();
          }
          reject();
        } catch (e) {
          reject();
          console.error('Can not init page.', e);
        } finally {
          this.iteratorPromise = null;
        }
      });
    }

    if (this.iteratorPromise !== null) {
      return this.iteratorPromise;
    }
  }

  protected setReadingItem(item: ReadingAtom[] | null | [null | ReadingAtom], clicked: boolean = false): void {
    if (!item) {
      return;
    }

    if (this.infoTagData && this.infoTagData.Language && this.infoTagData.Language.length > 0) {

      for (const word of item) {
        if (word) {
          const coveredZones = this.infoTagData.Language.filter(x => isRectIntersectRect(word.boundary, x.BBox));
          let resultLang: ILanguage | null = null;

          if (coveredZones.length === 1) {
            resultLang = this.langMapper(coveredZones[0].LangID);
          } else if (coveredZones.length > 1) {
            const prioritizedZone = coveredZones.map(x => ({
              LangID: x.LangID,
              square: (x.BBox.width - x.BBox.x) * (x.BBox.height - x.BBox.y)
            })).sort((a, b) => a.square - b.square)[0];
            resultLang = this.langMapper(prioritizedZone.LangID);
          }

          word.lang = resultLang !== null ? resultLang.code : null;
        }
      }
    }

    super.setReadingItem(item as ReadingAtom[], clicked);
  }

  private convertPdfCoordsToWindowCoords(x: number, y: number): any {
    if (!this.viewerAPI) { return null; }
    const docViewer = this.viewerAPI.Core.documentViewer;
    const doc = docViewer.getDocument();
    const displayMode = docViewer.getDisplayModeManager().getDisplayMode();

    // const pageIdx = this.getCurrentPageIdx() - 1; // need zero-indexed

    const viewerStart = doc.getViewerCoordinates(this.getCurrentPageIdx(), x, y);

    return displayMode.pageToWindow(viewerStart, this.getCurrentPageIdx());
  }

  public convertMouseCoordsIntoPdfCoords(e: MouseEvent): IPdfCoords | null {
    if (!this.viewerAPI) { return null; }
    const docViewer = this.viewerAPI.Core.documentViewer;
    const nativeClickEvent = e;
    const windowCoordinates = getMouseLocation(docViewer, nativeClickEvent);

    const displayMode = docViewer.getDisplayModeManager().getDisplayMode();
    const page = displayMode.getSelectedPages(windowCoordinates, windowCoordinates);
    const clickedPage = (page.first !== null) ? page.first : docViewer.getCurrentPage();

    const pdfCoords = this.convertWindowCoordsToPdfCoords(windowCoordinates, clickedPage);

    if (pdfCoords) {
      return {
        clientX: pdfCoords.x,
        clientY: pdfCoords.y,
        pageIdx: clickedPage
      };
    }

    return null;
  }

  private convertWindowCoordsToPdfCoords(windowCoords: { x: number, y: number }, pageIdx: number): { x: number, y: number } | null {
    if (!this.viewerAPI || pageIdx <= 0) { return null; }
    const docViewer = this.viewerAPI.Core.documentViewer;
    const displayMode = docViewer.getDisplayModeManager().getDisplayMode();
    const doc = docViewer.getDocument();
    const pageCoordinates = displayMode.windowToPage(windowCoords, pageIdx);
    return doc.getPDFCoordinates(pageIdx, pageCoordinates.x, pageCoordinates.y);
  }

  private getPageRect(pageIdx: number): ClientRect | DOMRect | undefined {
    if (!this.viewerAPI) { return; }
    const page = this.viewerAPI.UI.iframeWindow.document.querySelector(`#pageSection${pageIdx}`); // zero-indexed page index

    if (page) {
      const pageRect = page.getBoundingClientRect();
      return pageRect;
    }
  }

  disableTextSelection(): void {
    if (!this.viewerAPI) { return; }
    // this.viewerAPI.disableTextSelection();
    this.viewerAPI.UI.disableFeatures([this.viewerAPI.UI.Feature.TextSelection]);
  }

  enableTextSelection(): void {
    if (!this.viewerAPI) { return; }
    // this.viewerAPI.enableTextSelection();
    this.viewerAPI.UI.enableFeatures([this.viewerAPI.UI.Feature.TextSelection]);
  }

  async getDocumentUnsavedChanged(): Promise<IAutoSaveDocChanges> {
    // skip for new created html documents
    if (!this.docHash) {
      return Promise.reject();
    }
    const xfdfString = await this.getUnsavedChanged();
    return {
      unsavedChanges: xfdfString,
      documentHash: this.docHash,
      fileName: null,
      pageIdx: this.pageIdx,
      readingItemIdx: this.getReadingPostition(),
      readingItemPageIdx: this.pageIdx,
    };
  }

  private async getUnsavedChanged(): Promise<string | null> {
    if (this.annotationManager === null) { return null; }
    const annotList = this.annotationManager.getAnnotationsList().filter(annot => filterAnnotationExceptHighlights(annot));
    return await this.annotationManager.exportAnnotations({
      annotList,
    });
  }

  async applyDocumentChanges(docHash: string, xfdfString: string): Promise<void> {
    if (this.annotationManager === null) { return; }
    this.docHash = docHash;
    const allAnnots = this.annotationManager.getAnnotationsList().filter(annot => filterAnnotationExceptHighlights(annot));
    if (allAnnots && allAnnots.length !== 0) {
      this.annotationManager.deleteAnnotations(allAnnots, { imported: true, force: true });
    }
    if (xfdfString) {
      await this.annotationManager.importAnnotations(xfdfString);
    }
  }

  async lastReadingPositionFounded(pageIdx: number | null, readingItemIdx: number | null, readingItemPageIdx: number | null, itemType: ReadingUnitType): Promise<void> {
    if (readingItemIdx && readingItemPageIdx && this.viewerAPI) {
      const listener = async (...args) => {
        // indexed from 1
        // console.log('lastReadingPositionFounded', pageIdx, readingItemIdx, readingItemPageIdx);
        // console.log('lastReadingPositionFounded', args[0]);
        if (args[0] === readingItemPageIdx && this.viewerAPI) {
          // once page we looking for is founded
          // we unatach event listener
          this.viewerAPI.Core.documentViewer.removeEventListener('pageComplete', listener);
          //  for some reason pdftron sink errors appear there
          //  so lets take some small pause after naivation to page
          setTimeout(() => {
            this.initPage()
              .then(() => {
                if (this.textIterator && this.viewerAPI) {
                  const items = this.textIterator.getReadingItemByItemIdx(readingItemIdx);
                  if (items && items.length !== 0) {
                    this.setReadingItem(itemType === ReadingUnitType.Sentence ? items : [items[0]]);
                  }
                }
              })
              .catch((err) => {
                console.log('doc is not ready', err);
              });
          }, 1000);
        }
      };
      this.viewerAPI.Core.documentViewer.addEventListener('pageComplete', listener);
      this.setCurrentPage(readingItemPageIdx);
    } else if (pageIdx) {
      this.setCurrentPage(pageIdx);
    }
  }

  public async getWordIndexBeforeAnnot(annot: Core.Annotations.Annotation): Promise<number | null> {
    await this.updateAnnotations([annot]);
    if (this.textIterator === null) { return null; }

    const word = this.textIterator.getWordBeforeTextNote(annot.Id);
    if (word && !this.textIterator.isItemTextNote(word.itemIdx)) {
      return word.itemIdx;
    }
    return null;
  }

  public handleCurrentNavigationMode(currentMode: NavigationModes) {
    if (this.docViewer && this.viewerAPI) {
      if (currentMode === NavigationModes.CONTINUOUS) {
        this.viewerAPI.UI.setLayoutMode(PdfViewerLayoutModes.Continuous);
      } else if (currentMode === NavigationModes.PAGE_BY_PAGE) {
        this.viewerAPI.UI.setLayoutMode(PdfViewerLayoutModes.Single);
      }
    }
  }


  public triggerArrowUpDown(ev: KeyboardEvent) {
    // CoreControls.DocumentViewer.html#trigger__anchor
    // manually triggering docViewer.trigger(eventName, eventParams) doesnt work
    if (this.viewerAPI) {
      const elem = this.viewerAPI.Core.documentViewer.getScrollViewElement();
      if (elem) {
        super.triggerArrowUpDown(ev, elem);
      }
    }
  }

  public triggerPageUpDown(ev: KeyboardEvent) {
    if (this.viewerAPI) {
      switch (ev.key) {
        case 'PageUp':
          // this.viewerAPI.goToPrevPage();
          if (this.getCurrentPageIdx() > 0) {
            this.viewerAPI.Core.documentViewer.setCurrentPage(this.getCurrentPageIdx() - 1, false);
          }
          break;
        case 'PageDown':
          // this.viewerAPI.goToNextPage();
          if (this.getCurrentPageIdx() + 1 < this.viewerAPI.Core.documentViewer.getPageCount()) {
            this.viewerAPI.Core.documentViewer.setCurrentPage(this.getCurrentPageIdx() + 1, false);
          }
          break;
        default:
          return;
      }
    }
  }

}
